import React, { useState } from 'react';
import axios from 'axios';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { SmileySticker, ThumbsUp } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;
const QUICK_EMOJIS = ['👍', '❤️', '😄', '🎉', '🚀', '🔥', '👀', '✅', '😂', '💡', '🙌', '😢'];

// Jira/Asana-style reactions: shows existing emoji chips with counts + quick 👍
// and an emoji picker. Clicking an existing chip toggles the current user's vote.
export default function CommentReactions({ comment, userId, headers, onUpdated }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const reactions = comment.reactions || {};

  const react = async (emoji) => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/comments/${comment.comment_id}/react`, { emoji }, { withCredentials: true, headers });
      onUpdated?.(data);
    } catch (err) { console.error('react failed', err); }
    finally { setBusy(false); setPickerOpen(false); }
  };

  const entries = Object.entries(reactions).filter(([, users]) => Array.isArray(users) && users.length > 0);

  return (
    <div className="flex items-center gap-1 mt-1.5 flex-wrap" data-testid={`comment-reactions-${comment.comment_id}`}>
      {entries.map(([emoji, users]) => {
        const reacted = users.includes(userId);
        return (
          <button key={emoji} type="button" onClick={() => react(emoji)}
            title={reacted ? 'Remove your reaction' : 'Add your reaction'}
            data-testid={`reaction-chip-${comment.comment_id}-${emoji}`}
            className={`inline-flex items-center gap-1 h-6 px-2 rounded-full text-[11px] border transition-all ${reacted
              ? 'bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100'
              : 'bg-white border-zinc-200 text-zinc-600 hover:bg-zinc-50 hover:border-zinc-300'}`}>
            <span className="text-[12px] leading-none">{emoji}</span>
            <span className="font-semibold tabular-nums">{users.length}</span>
          </button>
        );
      })}

      {/* Quick thumbs-up (Asana-style) */}
      <button type="button" onClick={() => react('👍')} title="Like"
        data-testid={`reaction-like-${comment.comment_id}`}
        className="opacity-0 group-hover:opacity-100 h-6 w-6 rounded-full bg-white border border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50 flex items-center justify-center text-zinc-400 hover:text-blue-600 transition-all">
        <ThumbsUp size={11} weight="bold" />
      </button>

      {/* Emoji picker */}
      <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
        <PopoverTrigger asChild>
          <button type="button" title="Add reaction"
            data-testid={`reaction-picker-${comment.comment_id}`}
            className="opacity-0 group-hover:opacity-100 h-6 w-6 rounded-full bg-white border border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50 flex items-center justify-center text-zinc-400 hover:text-blue-600 transition-all">
            <SmileySticker size={12} weight="bold" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-1.5" data-testid="reaction-picker-popover">
          <div className="grid grid-cols-6 gap-0.5">
            {QUICK_EMOJIS.map(e => (
              <button key={e} type="button" onClick={() => react(e)}
                className="h-8 w-8 rounded hover:bg-zinc-100 text-[16px] leading-none transition-colors"
                data-testid={`reaction-emoji-${e}`}>
                {e}
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
